import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MINIMUM_POINTS_PER_MINUTE_TO_DISPLAY } from 'Pages/surveyExperience/components/surveyCard/constants';
import {
  CardContainer,
  CardCTA,
  CardDataDuration,
  CardDataPoints,
  CardDataPointsAmount,
  CardDataPointsPerMinute,
  CardDataSurveyId,
  CardDataWrapper,
  SteContent,
  SteFlatImage,
  SteImageContainer,
  SteOverflowHider,
  SurveyIdContainer,
  SurveyLink
} from 'Pages/surveyExperience/components/surveyCard/style';
import { SurveyButton, SurveyButtonDesign } from 'Pages/surveyExperience/components/surveyCard/SurveyButton';
import {
  SURVEY_CARD_TEST,
  SURVEY_EXCLUSIVE_CARD_TEST,
  SURVEY_LOCKED_CARD_TEST,
  SURVEY_POINTS_PER_MINUTE
} from 'Pages/surveyExperience/components/surveyCard/testId';
import { SurveyPromptEventProps } from 'Pages/surveyExperience/models/EventModels';
import { SurveyModel } from 'Pages/surveyExperience/models/SurveyModel';
import { dispatchQuestionnaireStartEvent, dispatchSurveyPromptEvent, dispatchSurveyRemoveEvent  } from 'Pages/surveyExperience/routes/dispatchEvents';
import {
  FirstPromptStateTypes,
  getSurveyPromptStatus,
  setViewedRouted
} from 'Pages/surveyExperience/services/surveyPromptService';
import { getLocalSurveyCache } from 'Pages/surveyExperience/services/surveyService';
import { ICON_IDS } from 'Shared/components/icons/constants';
import Icon from 'Shared/components/icons/icon';
import { ANALYTICS_EVENT_SUB_TYPE, ANALYTICS_SURVEY_LOAD_STATUSES } from 'Shared/services/analytics/constants';
import { analyticsTrackActivityClick } from 'Shared/services/analytics/events/surveyClick';
import { getValidatedSurveyId } from 'Shared/services/helperService';
import { SurveyQualificationDetails } from 'Shared/services/localStorageTypes';
import { getDynamicScreenerTestData } from 'Shared/services/opa/app/dynamicScreenerSplitTest';
import { navigateToExternalSurvey } from 'Shared/services/routingService';
import { getOutgoingSurveyUrl } from 'Shared/services/surveyRouting/app/surveyRoutingService';
import { UspUnansweredQuestions } from 'Shared/services/usp/models/UspUnansweredQuestionsModel';
import { deleteCachedSurveyQualificationDetails, getCachedSurveyQualificationDetails, prepareAndStoreSurveyQualificationCache } from 'Shared/utils/unansweredQuestionsQueue';

type surveyCardProps = {
  survey              : SurveyModel;
  locked?             : boolean;
  onClick?            : () => void;
  position            : number;
  highlight?          : boolean;
  className?          : string;
  buttonText?         : string;
  unansweredQuestions?: UspUnansweredQuestions[];
  requestInProgress?  : boolean;
  autoConfirmLegalPrompt?: boolean;
}

function isNonEmptyArray (array: any[] | null | undefined): array is any[] {
  return array ? array.length > 0 : false;
}

const showSurveyPrompt = (autoConfirmLegalPrompt: boolean): boolean => {
  return getSurveyPromptStatus() !== FirstPromptStateTypes.VIEWED_ROUTED && !autoConfirmLegalPrompt;
};

const showSurveyDescription = (survey: SurveyModel): boolean => {
  return (survey.description !== null && survey.description.length > 0 && survey.isDescVisible === true);
};

function SurveyCard({
  survey,
  onClick,
  position,
  className,
  buttonText,
  unansweredQuestions,
  autoConfirmLegalPrompt = false,
  requestInProgress,
}: surveyCardProps) {
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const {
    id,
    displayId,
    points,
    duration,
    attributes,
    internalUrl,
    providerId,
    locked
  } = survey;
  const { t }                            = useTranslation('surveyExperience');
  const hasUnansweredQuestions           = isNonEmptyArray(survey.unansweredQuestions);
  const isADM                            = attributes?.isAdm ?? false;
  const isExclusive                      = attributes?.isExclusive ?? false;
  const isADMStyle                       = isADM || isExclusive;
  const surveyLoadStatus                 = requestInProgress ? ANALYTICS_SURVEY_LOAD_STATUSES.in_progress : ANALYTICS_SURVEY_LOAD_STATUSES.complete;
  const surveyDynamicScreenerTest        = getDynamicScreenerTestData();
  const showDynamicScreenerQuestionnaire = surveyDynamicScreenerTest.enabled && surveyDynamicScreenerTest.providerIds.includes(survey.providerId);

  const isQuestionnaireAvailable = useCallback((): boolean => {
    if (showDynamicScreenerQuestionnaire) {
      return !!survey.unansweredDynamicQuestionIds?.length;
    }

    return hasUnansweredQuestions;
  }, [showDynamicScreenerQuestionnaire, survey.unansweredDynamicQuestionIds, hasUnansweredQuestions]);

  const renderTopLeftContent = (): JSX.Element => {
    if (isADMStyle) {
      return (
        <SteImageContainer>
          <SteOverflowHider>
            <SteContent>
              <SteFlatImage />
              <SurveyIdContainer>
                {renderSurveyId()}
              </SurveyIdContainer>
            </SteContent>
          </SteOverflowHider>
        </SteImageContainer>
      );
    }

    return renderSurveyId();
  };

  const renderSurveyId = (): JSX.Element => {
    return (
      <CardDataSurveyId>
        <span>{t('SurveyCard--element-surveyIdLabel')}</span>
        {getValidatedSurveyId(displayId)}
      </CardDataSurveyId>
    );
  };

  const renderCardContent = (): JSX.Element | undefined => {
    const pointsPerMinute = points / duration;
    const isFinitePointsPerMinute = Number.isFinite(pointsPerMinute);

    return (
      <CardDataWrapper>
        {renderTopLeftContent()}
        <CardDataPoints>
          <CardDataPointsAmount>
            <span>{points}</span> {t('SurveyCard--element-pointsSuffix')}
          </CardDataPointsAmount>
          <CardDataPointsPerMinute>
            {isFinitePointsPerMinute && pointsPerMinute >= MINIMUM_POINTS_PER_MINUTE_TO_DISPLAY && (
              <>
                <span data-testid={SURVEY_POINTS_PER_MINUTE}>
                  {Math.round(pointsPerMinute)}
                </span>
                {t('SurveyCard--element-pointsPerMin')}
              </>
            )}
          </CardDataPointsPerMinute>
        </CardDataPoints>
        <CardDataDuration>
          <Icon iconId={ICON_IDS.TIMER_OUTLINE} />
          {t('SurveyCard--element-duration', {minutes: duration})}
        </CardDataDuration>
      </CardDataWrapper>
    );
  };

  const getButtonDesign = (): SurveyButtonDesign => {
    if (locked) {
      return SurveyButtonDesign.LOCKED;
    } else if (isADMStyle) {
      return SurveyButtonDesign.ADM;
    } else {
      return SurveyButtonDesign.DEFAULT;
    }
  };

  const surveyClickCallback = useCallback((event?: React.MouseEvent): void => {
    const isModifierClick = event?.metaKey || event?.ctrlKey;

    if (isModifierClick && !showSurveyPrompt(autoConfirmLegalPrompt) && !showSurveyDescription(survey) && !isQuestionnaireAvailable()) {
      dispatchSurveyRemoveEvent({ surveyId: id });
      setIsHidden(true);
    }

    analyticsTrackActivityClick({
      id                  : id,
      points              : points,
      provider_id         : providerId,
      loi                 : duration,
      position            : position,
      sub_type            : ANALYTICS_EVENT_SUB_TYPE.survey_click,
      has_prequalification: isQuestionnaireAvailable(),
      survey_count        : getLocalSurveyCache().length,
      survey_load_status  : surveyLoadStatus
    });
  }, [autoConfirmLegalPrompt, survey, isQuestionnaireAvailable, id, points, providerId, duration, position, surveyLoadStatus]);

  const openModalSurveyQualification = useCallback((): void => {
    if (showDynamicScreenerQuestionnaire) {
      const details: SurveyQualificationDetails | null = getCachedSurveyQualificationDetails();
      surveyClickCallback();
      if (!details || details.questionnaireDetails.questions.length === 0) {
        dispatchSurveyRemoveEvent({ surveyId: id });
        navigateToExternalSurvey(internalUrl, true, true);
        return;
      }

      dispatchQuestionnaireStartEvent({
        ...details.questionnaireDetails,
        isModal: true,
        surveyId: survey.id,
        surveyProviderId: survey.providerId,
        surveyUrl: internalUrl,
        isDynamicScreener: true
      });
      deleteCachedSurveyQualificationDetails();
      return;
    }

    const details: SurveyQualificationDetails | null = getCachedSurveyQualificationDetails();
    surveyClickCallback();
    if (!details) {
      dispatchSurveyRemoveEvent({ surveyId: id });
      navigateToExternalSurvey(internalUrl, true, true);
      return;
    }

    dispatchQuestionnaireStartEvent({
      ...details.questionnaireDetails,
      isModal: true,
      surveyId: survey.id,
      surveyProviderId: survey.providerId,
      surveyUrl: internalUrl
    });
    deleteCachedSurveyQualificationDetails();
  }, [surveyClickCallback, showDynamicScreenerQuestionnaire, survey.id, survey.providerId, internalUrl, id]);

  const openQuestionnaire = useCallback(() => {
    try {
      const surveyQualificationUnansweredQuestions = unansweredQuestions || [];

      prepareAndStoreSurveyQualificationCache({
        survey,
        unansweredQuestions: surveyQualificationUnansweredQuestions,
        isDynamicScreener: showDynamicScreenerQuestionnaire
      });

      openModalSurveyQualification();
    } catch {
      navigateToExternalSurvey(internalUrl, true, true);
    }
  }, [internalUrl, showDynamicScreenerQuestionnaire, openModalSurveyQualification, survey, unansweredQuestions]);

  const handleClick = useCallback((): void => {
    if (locked) {
      return;
    }

    if (autoConfirmLegalPrompt) {
      setViewedRouted();
    }

    if (showSurveyPrompt(autoConfirmLegalPrompt) || showSurveyDescription(survey)) {
      const surveyPromptProps: SurveyPromptEventProps = {
        url               : internalUrl,
        surveyId          : id,
        surveyPoints      : points,
        surveyLOI         : duration,
        surveyProviderId  : providerId,
        surveyPosition    : position,
        surveyLoadStatus  : surveyLoadStatus,
        description       : survey.description,
        descriptionVisible: survey.isDescVisible
      };

      if (isQuestionnaireAvailable()) {
        surveyPromptProps.onAccept = openQuestionnaire;
      }

      if (onClick) {
        const oldOnAccept = surveyPromptProps.onAccept;
        surveyPromptProps.onAccept = () => {
          if (oldOnAccept) {
            oldOnAccept();
          } else {
            navigateToExternalSurvey(internalUrl, true, true);
          }
          onClick();
        };
      }

      dispatchSurveyPromptEvent(surveyPromptProps);
    } else if (isQuestionnaireAvailable()) {
      openQuestionnaire();
      onClick?.();
    } else {
      dispatchSurveyRemoveEvent({ surveyId: id });
      onClick?.();
      setIsHidden(true);
    }
  }, [locked, autoConfirmLegalPrompt, survey, isQuestionnaireAvailable, internalUrl, id, points, duration, providerId, position, surveyLoadStatus, onClick, openQuestionnaire]);

  const preventRightClick = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.preventDefault();
  }, []);

  const renderSurveyButton = (): JSX.Element => {
    return(
      <SurveyButton design = {getButtonDesign()}>
        {buttonText}
      </SurveyButton>
    );
  };

  const renderCardContainer = (): JSX.Element => {
    return (
      <CardContainer
        data-testid   = {getCardTestId()}
        locked        = {locked}
        onContextMenu = {preventRightClick}
        className     = {className}
        onClick       = {handleClick}
      >
        {renderCardContent()}
        <CardCTA>
          {renderSurveyButton()}
        </CardCTA>
      </CardContainer>
    );
  };

  const renderSurveyCard = (): JSX.Element => {
    if ((showSurveyPrompt(autoConfirmLegalPrompt)) || showSurveyDescription(survey) || isQuestionnaireAvailable() || locked) {
      return renderCardContainer();
    }

    return(
      <SurveyLink
        href    = {getOutgoingSurveyUrl(internalUrl, true, !!survey.attributes.isAutoEmail)} target = '_blank' rel = "noreferrer"
        onClick = {surveyClickCallback}
      >
        {renderCardContainer()}
      </SurveyLink>
    );
  };

  const getCardTestId = (): string => {
    if (locked) {
      return SURVEY_LOCKED_CARD_TEST;
    } else if (isADMStyle) {
      return SURVEY_EXCLUSIVE_CARD_TEST;
    }

    return SURVEY_CARD_TEST;
  };

  if (isHidden) {
    return <></>;
  }

  return (
    <>
      {renderSurveyCard()}
    </>
  );
}

export default SurveyCard;
